import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue, set, remove, update } from "firebase/database";
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  limit,
  deleteField as firestoreDeleteField,
} from "firebase/firestore";

const MAX_CONTEXT_FIELDS = 5;
const MAX_TOTAL_FIELDS = 10;

const STANDARD_COLUMN_LABELS = {
  business_name: "Business Name",
  lead_description: "Description",
  lead_email: "Email",
  lead_telephone: "Phone",
  lead_website: "Website",
  lead_rating: "Rating",
  lead_instagram_followers: "IG Followers",
  lead_reviews: "Reviews",
  lead_socials: "Socials",
  lead_decision_makers: "Decision Makers",
  import_name: "Import",
  lead_template: "Template",
  lead_reply: "Reply",
  lead_score: "Lead Score",
};

const DEFAULT_REORDERABLE_ORDER = [
  "business_name", "lead_description", "lead_email", "lead_telephone", "lead_website",
  "lead_rating", "lead_instagram_followers", "lead_reviews", "lead_socials", "lead_decision_makers",
  "import_name",
  "lead_template", "lead_reply", "lead_score",
];

const FIELD_TYPES = {
  text: { label: "Text", icon: "T" },
  number: { label: "Number", icon: "#" },
};

function generateFieldKey(label) {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `cf_${slug}`;
}

// ── Per-account layout cache ──────────────────────────────────────────────
// We persist the custom-field definitions + saved column order in localStorage,
// keyed by the world's userId (the World/{userId} owner). This lets the table
// render its final column layout instantly on the next load — the header labels
// no longer "jump" while we wait for the Realtime DB, and the Realtime DB result
// simply confirms (or quietly corrects) the cached layout afterwards.
// Keying by userId keeps each world/account isolated, so team worlds never mix.
const LAYOUT_CACHE_PREFIX = "wdb_layout_cache_";

function readLayoutCache(userId) {
  if (!userId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${LAYOUT_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.fields)) return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeLayoutCache(userId, fields, order) {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${LAYOUT_CACHE_PREFIX}${userId}`,
      JSON.stringify({ fields, order: order ?? null })
    );
  } catch {
    /* localStorage unavailable / quota — ignore, fall back to live load */
  }
}

export default function useCustomFields(userId) {
  // Seed synchronously from the per-account cache so the first render already
  // has the correct columns (no flicker on warm loads).
  const initialCache = readLayoutCache(userId);
  const [allCustomFields, setAllCustomFields] = useState(
    initialCache ? initialCache.fields : []
  );
  const [savedColumnOrder, setSavedColumnOrder] = useState(
    initialCache ? initialCache.order ?? null : null
  );
  // Track the first snapshot of BOTH the custom-field definitions and the saved
  // column order separately, so consumers only render once everything needed to
  // build the final column layout has arrived (prevents a flicker where columns
  // load first and custom fields get appended a moment later). A warm cache
  // counts as "loaded" immediately.
  const [fieldsLoaded, setFieldsLoaded] = useState(!!initialCache);
  const [orderLoaded, setOrderLoaded] = useState(!!initialCache);
  const loading = !!userId && (!fieldsLoaded || !orderLoaded);

  const allCustomFieldsRef = useRef([]);
  const savedColumnOrderRef = useRef(null);

  useEffect(() => { allCustomFieldsRef.current = allCustomFields; }, [allCustomFields]);
  useEffect(() => { savedColumnOrderRef.current = savedColumnOrder; }, [savedColumnOrder]);

  const customFields = useMemo(
    () => allCustomFields.filter((f) => !f.hidden),
    [allCustomFields]
  );
  const hiddenFields = useMemo(
    () => allCustomFields.filter((f) => f.hidden),
    [allCustomFields]
  );

  useEffect(() => {
    if (!userId) {
      setAllCustomFields([]);
      setFieldsLoaded(true);
      return;
    }

    // Warm cache → treat as loaded right away; live snapshot confirms later.
    const cached = readLayoutCache(userId);
    if (cached) {
      setAllCustomFields(cached.fields);
      setFieldsLoaded(true);
    } else {
      setFieldsLoaded(false);
    }

    const cfRef = ref(database, `World/${userId}/settings/customFields`);
    const unsub = onValue(cfRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data)
          .map(([id, field]) => ({ id, ...field }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setAllCustomFields(list);
      } else {
        setAllCustomFields([]);
      }
      setFieldsLoaded(true);
    });

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    if (!userId) { setSavedColumnOrder(null); setOrderLoaded(true); return; }

    const cached = readLayoutCache(userId);
    if (cached) {
      setSavedColumnOrder(cached.order ?? null);
      setOrderLoaded(true);
    } else {
      setOrderLoaded(false);
    }

    const orderRef = ref(database, `World/${userId}/settings/columnOrder`);
    const unsub = onValue(orderRef, (snap) => {
      setSavedColumnOrder(snap.exists() ? snap.val() : null);
      setOrderLoaded(true);
    });
    return () => unsub();
  }, [userId]);

  // Persist the resolved layout once both pieces are loaded, so the next load
  // can render the correct columns synchronously from cache.
  useEffect(() => {
    if (!userId || loading) return;
    writeLayoutCache(userId, allCustomFields, savedColumnOrder);
  }, [userId, loading, allCustomFields, savedColumnOrder]);

  const saveColumnOrder = useCallback(async (orderedKeys) => {
    if (!userId) return;
    try {
      const orderRef = ref(database, `World/${userId}/settings/columnOrder`);
      await set(orderRef, orderedKeys);
    } catch (err) {
      console.error("Error saving column order:", err);
    }
  }, [userId]);

  const addField = useCallback(
    async (label, useAsEmailContext = false, fieldType = "text") => {
      if (!userId || !label.trim()) return { success: false, error: "Invalid input" };

      try {
        const allFields = allCustomFieldsRef.current;

        if (allFields.length >= MAX_TOTAL_FIELDS) {
          return {
            success: false,
            error: `Maximum ${MAX_TOTAL_FIELDS} custom fields allowed (including hidden).`,
          };
        }

        const key = generateFieldKey(label);

        const existing = allFields.find((f) => f.key === key);
        if (existing) {
          return {
            success: false,
            error: existing.hidden
              ? "A hidden field with this name already exists. Restore it or delete it first."
              : "A field with this name already exists.",
          };
        }

        const activeContextCount = allFields.filter(
          (f) => !f.hidden && f.useAsEmailContext
        ).length;
        if (useAsEmailContext && activeContextCount >= MAX_CONTEXT_FIELDS) {
          return {
            success: false,
            error: `Maximum ${MAX_CONTEXT_FIELDS} fields can be used as email context.`,
          };
        }

        const validType = FIELD_TYPES[fieldType] ? fieldType : "text";
        const id = `cf_${Date.now()}`;
        const newField = {
          key,
          label: label.trim(),
          useAsEmailContext,
          fieldType: validType,
          hidden: false,
          createdAt: Date.now(),
          order: allFields.length,
        };

        const cfRef = ref(database, `World/${userId}/settings/customFields/${id}`);
        await set(cfRef, newField);

        const currentOrder = savedColumnOrderRef.current;
        if (currentOrder && Array.isArray(currentOrder)) {
          const newOrder = [...currentOrder];
          const templateIdx = newOrder.indexOf("lead_template");
          if (templateIdx >= 0) {
            newOrder.splice(templateIdx, 0, key);
          } else {
            newOrder.push(key);
          }
          await saveColumnOrder(newOrder);
        }

        return { success: true, id, key };
      } catch (err) {
        console.error("Error adding custom field:", err);
        return { success: false, error: "Failed to add field. Please try again." };
      }
    },
    [userId, saveColumnOrder]
  );

  const updateField = useCallback(
    async (fieldId, updates) => {
      if (!userId || !fieldId) return { success: false, error: "Invalid input" };

      try {
        if (updates.useAsEmailContext === true) {
          const activeContextCount = allCustomFieldsRef.current.filter(
            (f) => !f.hidden && f.useAsEmailContext && f.id !== fieldId
          ).length;
          if (activeContextCount >= MAX_CONTEXT_FIELDS) {
            return {
              success: false,
              error: `Maximum ${MAX_CONTEXT_FIELDS} fields can be used as email context.`,
            };
          }
        }

        const cfRef = ref(database, `World/${userId}/settings/customFields/${fieldId}`);
        await update(cfRef, updates);
        return { success: true };
      } catch (err) {
        console.error("Error updating custom field:", err);
        return { success: false, error: "Failed to update field." };
      }
    },
    [userId]
  );

  const hideField = useCallback(
    async (fieldId) => {
      if (!userId || !fieldId) return { success: false, error: "Invalid input" };

      try {
        const field = allCustomFieldsRef.current.find((f) => f.id === fieldId);
        if (!field) return { success: false, error: "Field not found" };

        const cfRef = ref(database, `World/${userId}/settings/customFields/${fieldId}`);
        await update(cfRef, { hidden: true, useAsEmailContext: false });

        const currentOrder = savedColumnOrderRef.current;
        if (currentOrder && Array.isArray(currentOrder)) {
          await saveColumnOrder(currentOrder.filter((k) => k !== field.key));
        }

        return { success: true };
      } catch (err) {
        console.error("Error hiding custom field:", err);
        return { success: false, error: "Failed to hide field." };
      }
    },
    [userId, saveColumnOrder]
  );

  const unhideField = useCallback(
    async (fieldId) => {
      if (!userId || !fieldId) return { success: false, error: "Invalid input" };

      try {
        const field = allCustomFieldsRef.current.find((f) => f.id === fieldId);
        if (!field) return { success: false, error: "Field not found" };

        const cfRef = ref(database, `World/${userId}/settings/customFields/${fieldId}`);
        await update(cfRef, { hidden: false });

        const currentOrder = savedColumnOrderRef.current;
        if (currentOrder && Array.isArray(currentOrder)) {
          const newOrder = [...currentOrder];
          const templateIdx = newOrder.indexOf("lead_template");
          if (templateIdx >= 0) {
            newOrder.splice(templateIdx, 0, field.key);
          } else {
            newOrder.push(field.key);
          }
          await saveColumnOrder(newOrder);
        }

        return { success: true };
      } catch (err) {
        console.error("Error restoring custom field:", err);
        return { success: false, error: "Failed to restore field." };
      }
    },
    [userId, saveColumnOrder]
  );

  const deleteFieldPermanently = useCallback(
    async (fieldId) => {
      if (!userId || !fieldId) return { success: false, error: "Invalid input" };

      try {
        const field = allCustomFieldsRef.current.find((f) => f.id === fieldId);
        if (!field) return { success: false, error: "Field not found" };

        if (!field.hidden) {
          return { success: false, error: "Field must be hidden before it can be permanently deleted." };
        }

        const dbRef = collection(firestore, `World/${userId}/db`);
        const BATCH_SIZE = 500;
        let hasMore = true;

        while (hasMore) {
          const q = query(dbRef, where(field.key, "!=", null), limit(BATCH_SIZE));
          const snapshot = await getDocs(q);

          if (snapshot.empty) {
            hasMore = false;
            break;
          }

          const batch = writeBatch(firestore);
          snapshot.docs.forEach((d) => {
            batch.update(d.ref, { [field.key]: firestoreDeleteField() });
          });
          await batch.commit();

          if (snapshot.docs.length < BATCH_SIZE) hasMore = false;
        }

        const cfRef = ref(database, `World/${userId}/settings/customFields/${fieldId}`);
        await remove(cfRef);

        const currentOrder = savedColumnOrderRef.current;
        if (currentOrder && Array.isArray(currentOrder)) {
          await saveColumnOrder(currentOrder.filter((k) => k !== field.key));
        }

        return { success: true };
      } catch (err) {
        console.error("Error permanently deleting custom field:", err);
        return { success: false, error: "Failed to delete field. Please try again." };
      }
    },
    [userId, saveColumnOrder]
  );

  const reorderFields = useCallback(
    async (orderedIds) => {
      if (!userId) return;
      try {
        const updates = {};
        orderedIds.forEach((id, index) => {
          updates[`World/${userId}/settings/customFields/${id}/order`] = index;
        });
        await update(ref(database), updates);
      } catch (err) {
        console.error("Error reordering custom fields:", err);
      }
    },
    [userId]
  );

  const contextFields = useMemo(
    () => customFields.filter((f) => f.useAsEmailContext),
    [customFields]
  );

  return {
    customFields,
    hiddenFields,
    contextFields,
    loading,
    addField,
    updateField,
    hideField,
    unhideField,
    deleteFieldPermanently,
    reorderFields,
    saveColumnOrder,
    savedColumnOrder,
    MAX_CONTEXT_FIELDS,
    MAX_TOTAL_FIELDS,
  };
}

export {
  generateFieldKey,
  MAX_CONTEXT_FIELDS,
  MAX_TOTAL_FIELDS,
  STANDARD_COLUMN_LABELS,
  DEFAULT_REORDERABLE_ORDER,
  FIELD_TYPES,
};
